import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import LazyLoad from 'react-lazy-load'

import { Box, Flex, Heading, Link } from 'pcln-design-system'

import {
  AtlantaSVG,
  DallasSVG,
  DenverSVG,
  MiamiSVG,
  NewYorkSVG,
  LosAngelesSVG,
  OrlandoSVG,
  SanDiegoSVG,
  SanFranciscoSVG,
  SeattleSVG
} from '../graphic/index'

import { ImageTile } from '@pcln/content-tile'

import { generateLinkArray } from './helpers/link-utilities'

import CONTENT from '../../constants/Content'

const { home = {} } = CONTENT
const { popularDestinationsV2 = {} } = home
const { heading = '', data = {} } = popularDestinationsV2
const { firstGroup, secondGroup, thirdGroup, fourthGroup } = data

const PopularDestinationsBox = styled(Box)`
  display: none;

  @media (min-width: 768px) {
    display: block;
  }
`

const ImageTileFrame = styled(Flex)`
  min-width: 600px;

  a {
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
  }

  a:hover {
    text-decoration: underline;
    color: ${props => props.theme.colors.white};
  }
`

const ImageTileBox = styled(Box)`
  position: relative;
`

const ImageTileLink = styled(Link)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`

const PopularDestinations = ({ routeLocationObject }) => {
  const groupOneFirstLinkArray = generateLinkArray({
    staySearchID: firstGroup[0].staySearchID,
    driveSearchCity: firstGroup[0].driveSearchCity,
    airSearchCityAirport: firstGroup[0].airSearchCityAirport,
    hotelSEOhref: firstGroup[0].href,
    routeLocationObject
  })

  const groupOneSecondLinkArray = generateLinkArray({
    staySearchID: firstGroup[1].staySearchID,
    driveSearchCity: firstGroup[1].driveSearchCity,
    airSearchCityAirport: firstGroup[1].airSearchCityAirport,
    hotelSEOhref: firstGroup[1].href,
    routeLocationObject
  })

  const groupTwoFirstLinkArray = generateLinkArray({
    staySearchID: secondGroup[0].staySearchID,
    driveSearchCity: secondGroup[0].driveSearchCity,
    airSearchCityAirport: secondGroup[0].airSearchCityAirport,
    hotelSEOhref: secondGroup[0].href,
    routeLocationObject
  })

  const groupTwoSecondLinkArray = generateLinkArray({
    staySearchID: secondGroup[1].staySearchID,
    driveSearchCity: secondGroup[1].driveSearchCity,
    airSearchCityAirport: secondGroup[1].airSearchCityAirport,
    hotelSEOhref: secondGroup[1].href,
    routeLocationObject
  })

  const groupTwoThirdLinkArray = generateLinkArray({
    staySearchID: secondGroup[2].staySearchID,
    driveSearchCity: secondGroup[2].driveSearchCity,
    airSearchCityAirport: secondGroup[2].airSearchCityAirport,
    hotelSEOhref: secondGroup[2].href,
    routeLocationObject
  })

  const groupThreeFirstLinkArray = generateLinkArray({
    staySearchID: thirdGroup[0].staySearchID,
    driveSearchCity: thirdGroup[0].driveSearchCity,
    airSearchCityAirport: thirdGroup[0].airSearchCityAirport,
    hotelSEOhref: thirdGroup[0].href,
    routeLocationObject
  })

  const groupThreeSecondLinkArray = generateLinkArray({
    staySearchID: thirdGroup[1].staySearchID,
    driveSearchCity: thirdGroup[1].driveSearchCity,
    airSearchCityAirport: thirdGroup[1].airSearchCityAirport,
    hotelSEOhref: thirdGroup[1].href,
    routeLocationObject
  })

  const groupFourFirstLinkArray = generateLinkArray({
    staySearchID: fourthGroup[0].staySearchID,
    driveSearchCity: fourthGroup[0].driveSearchCity,
    airSearchCityAirport: fourthGroup[0].airSearchCityAirport,
    hotelSEOhref: fourthGroup[0].href,
    routeLocationObject
  })

  const groupFourSecondLinkArray = generateLinkArray({
    staySearchID: fourthGroup[1].staySearchID,
    driveSearchCity: fourthGroup[1].driveSearchCity,
    airSearchCityAirport: fourthGroup[1].airSearchCityAirport,
    hotelSEOhref: fourthGroup[1].href,
    routeLocationObject
  })

  const groupFourThirdLinkArray = generateLinkArray({
    staySearchID: fourthGroup[2].staySearchID,
    driveSearchCity: fourthGroup[2].driveSearchCity,
    airSearchCityAirport: fourthGroup[2].airSearchCityAirport,
    hotelSEOhref: fourthGroup[2].href,
    routeLocationObject
  })

  return (
    <PopularDestinationsBox>
      <Heading mx={3} mt={5} mb={2} textAlign='left' color='text' fontSize={4} fontWeight='bold'>
        {heading}
      </Heading>
      <ImageTileFrame mx={2} flexWrap='wrap'>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 2 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${firstGroup[0]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={firstGroup[0].image}
              imageWebP={firstGroup[0].imageWebP}
              height='301px'
              title={firstGroup[0].title}
              navArray={groupOneFirstLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <NewYorkSVG />
              <ImageTileLink
                href={groupOneFirstLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_NEW_YORK'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${firstGroup[1]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={firstGroup[1].image}
              imageWebP={firstGroup[1].imageWebP}
              height='301px'
              title={firstGroup[1].title}
              navArray={groupOneSecondLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <LosAngelesSVG />
              <ImageTileLink
                href={groupOneSecondLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_LOS_ANGELES'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${secondGroup[0]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={secondGroup[0].image}
              imageWebP={secondGroup[0].imageWebP}
              height='301px'
              title={secondGroup[0].title}
              navArray={groupTwoFirstLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <OrlandoSVG />
              <ImageTileLink
                href={groupTwoFirstLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_ORLANDO'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${secondGroup[1]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={secondGroup[1].image}
              imageWebP={secondGroup[1].imageWebP}
              height='301px'
              title={secondGroup[1].title}
              navArray={groupTwoSecondLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <AtlantaSVG />
              <ImageTileLink
                href={groupTwoSecondLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_ATLANTA'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${secondGroup[2]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={secondGroup[2].image}
              imageWebP={secondGroup[2].imageWebP}
              height='301px'
              title={secondGroup[2].title}
              navArray={groupTwoThirdLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <DallasSVG />
              <ImageTileLink
                href={groupTwoThirdLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_DALLAS'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${thirdGroup[0]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={thirdGroup[0].image}
              imageWebP={thirdGroup[0].imageWebP}
              height='301px'
              title={thirdGroup[0].title}
              navArray={groupThreeFirstLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <SanFranciscoSVG />
              <ImageTileLink
                href={groupThreeFirstLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_SAN_FRANCISCO'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 2 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${thirdGroup[1]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={thirdGroup[1].image}
              imageWebP={thirdGroup[1].imageWebP}
              height='301px'
              title={thirdGroup[1].title}
              navArray={groupThreeSecondLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <MiamiSVG />
              <ImageTileLink
                href={groupThreeSecondLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_MIAMI'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${fourthGroup[0]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={fourthGroup[0].image}
              imageWebP={fourthGroup[0].imageWebP}
              height='301px'
              title={fourthGroup[0].title}
              navArray={groupFourFirstLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <DenverSVG />
              <ImageTileLink
                href={groupFourFirstLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_DENVER'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${fourthGroup[1]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={fourthGroup[1].image}
              imageWebP={fourthGroup[1].imageWebP}
              height='301px'
              title={fourthGroup[1].title}
              navArray={groupFourSecondLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <SeattleSVG />
              <ImageTileLink
                href={groupFourSecondLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_SEATTLE'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
        <ImageTileBox
          p={2}
          width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
          data-ga-category='PCLN_HOME'
          data-ga-action='SEO_Link'
          data-ga-label={`Generic Tile Click | ${fourthGroup[2]
            .driveSearchCity}`}
        >
          <LazyLoad height={301} debounce={false} throttle={50}>
            <ImageTile
              image={fourthGroup[2].image}
              imageWebP={fourthGroup[2].imageWebP}
              height='301px'
              title={fourthGroup[2].title}
              navArray={groupFourThirdLinkArray}
              handleNavOnClick={href => window.open(href)}
            >
              <SanDiegoSVG />
              <ImageTileLink
                href={groupFourThirdLinkArray[0].href}
                target='_blank'
                data-autobot-element-id='POPULAR_DESTINATIONS_SAN_DIEGO'
              />
            </ImageTile>
          </LazyLoad>
        </ImageTileBox>
      </ImageTileFrame>
    </PopularDestinationsBox>
  )
}

PopularDestinations.displayName = 'PopularDestinations'

PopularDestinations.defaultProps = {
  routeLocationObject: {}
}

PopularDestinations.propTypes = {
  routeLocationObject: PropTypes.object
}

export default PopularDestinations
